import React, { useState, useEffect } from 'react';
import { useMapStore } from '../store/useMapStore';
import h3Config from '../../../shared/h3-config';

export const DebugOverlay: React.FC = () => {
  const viewport = useMapStore((state) => state.viewport);
  const showDebugHexagons = useMapStore((state) => state.showDebugHexagons);
  const toggleDebugHexagons = useMapStore((state) => state.toggleDebugHexagons);
  const h3DebugCells = useMapStore((state) => state.h3DebugCells);
  
  const [viewportStats, setViewportStats] = useState<{
    viewportSqKm: number;
    res2Stats: { count: number; totalSqKm: number; percentage: number } | null;
    aggStats: { count: number; totalSqKm: number; percentage: number; resolution: number } | null;
  } | null>(null);
  
  // Calculate viewport and H3 statistics
  useEffect(() => {
    const calculateStats = async () => {
      try {
        // Import H3 functions
        const { getHexagonAreaAvg, cellToChildren, polygonToCellsExperimental } = await import('h3-js');
        
        // Calculate viewport bounds area (approximate using lat/lon degrees)
        // At the equator, 1 degree ≈ 111 km
        // Adjust for latitude using cosine
        const latRadians = viewport.latitude * Math.PI / 180;
        const kmPerDegreeLat = 111;
        const kmPerDegreeLon = 111 * Math.cos(latRadians);
        
        // Estimate viewport size based on zoom level
        // This is an approximation - actual viewport depends on screen size
        const worldSize = 256 * Math.pow(2, viewport.zoom);
        const lonDegreesPerPixel = 360 / worldSize;
        const latDegreesPerPixel = (360 / worldSize) / Math.cos(latRadians);
        
        // Assume typical viewport size (adjust based on your needs)
        const viewportWidthPixels = window.innerWidth;
        const viewportHeightPixels = window.innerHeight;
        
        const lonSpan = viewportWidthPixels * lonDegreesPerPixel;
        const latSpan = viewportHeightPixels * latDegreesPerPixel;
        
        const widthKm = lonSpan * kmPerDegreeLon;
        const heightKm = latSpan * kmPerDegreeLat;
        const viewportSqKm = widthKm * heightKm;
        
        // Calculate res 2 stats
        let res2Stats = null;
        if (h3DebugCells.length > 0) {
          const res2AreaKm2 = getHexagonAreaAvg(2, 'km2');
          const res2Count = h3DebugCells.length;
          const res2TotalSqKm = res2Count * res2AreaKm2;
          const res2Percentage = (res2TotalSqKm / viewportSqKm) * 100;
          
          res2Stats = {
            count: res2Count,
            totalSqKm: res2TotalSqKm,
            percentage: res2Percentage,
          };
        }
        
        // Calculate aggregation level stats
        // Get the actual aggregation resolution from h3Config
        const { aggRes } = h3Config.getSettingsForZoom(viewport.zoom);
        
        let aggStats = null;
        if (h3DebugCells.length > 0 && aggRes && aggRes > 2) {
          // Calculate viewport bounds with padding (matching MapBackground logic)
          const latPadding = latSpan * 0.1;
          const lonPadding = lonSpan * 0.1;
          
          const north = Math.min(viewport.latitude + latSpan / 2 + latPadding, 85);
          const south = Math.max(viewport.latitude - latSpan / 2 - latPadding, -85);
          const east = viewport.longitude + lonSpan / 2 + lonPadding;
          const west = viewport.longitude - lonSpan / 2 - lonPadding;
          
          // Get agg cells that cover the viewport (matching API logic)
          const polygon: [number, number][] = [
            [north, west],
            [north, east],
            [south, east],
            [south, west],
            [north, west],
          ];
          
          try {
            // Use experimental overlapping mode like the API does
            const aggResCells = polygonToCellsExperimental(polygon, aggRes, 'containmentOverlapping');
            const aggResCellsSet = new Set(aggResCells);
            
            console.log('[DebugOverlay] Agg calculation', {
              aggRes,
              viewportAggCells: aggResCells.length,
              r2Cells: h3DebugCells.length,
            });
            
            // Filter to only agg cells that are children of the r2 cells (matching API logic)
            let totalAggCells = 0;
            for (const res2Cell of h3DebugCells) {
              const children = cellToChildren(res2Cell, aggRes);
              const relevantChildren = children.filter(cell => aggResCellsSet.has(cell));
              totalAggCells += relevantChildren.length;
            }
            
            console.log('[DebugOverlay] Total agg cells after filtering', totalAggCells);
            
            const aggAreaKm2 = getHexagonAreaAvg(aggRes, 'km2');
            const aggTotalSqKm = totalAggCells * aggAreaKm2;
            const aggPercentage = (aggTotalSqKm / viewportSqKm) * 100;
            
            aggStats = {
              count: totalAggCells,
              totalSqKm: aggTotalSqKm,
              percentage: aggPercentage,
              resolution: aggRes,
            };
          } catch (error) {
            console.warn('[DebugOverlay] Failed to calculate agg cells with polygonToCellsExperimental, using fallback', error);
            // Fallback: just count all children
            let totalAggCells = 0;
            for (const res2Cell of h3DebugCells) {
              const children = cellToChildren(res2Cell, aggRes);
              totalAggCells += children.length;
            }
            
            const aggAreaKm2 = getHexagonAreaAvg(aggRes, 'km2');
            const aggTotalSqKm = totalAggCells * aggAreaKm2;
            const aggPercentage = (aggTotalSqKm / viewportSqKm) * 100;
            
            aggStats = {
              count: totalAggCells,
              totalSqKm: aggTotalSqKm,
              percentage: aggPercentage,
              resolution: aggRes,
            };
          }
        }
        
        setViewportStats({
          viewportSqKm,
          res2Stats,
          aggStats,
        });
      } catch (error) {
        console.error('[DebugOverlay] Failed to calculate stats', error);
      }
    };
    
    calculateStats();
  }, [viewport, h3DebugCells]);

  return (
    <div
      style={{
        position: 'absolute',
        bottom: '20px',
        right: '20px',
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        color: '#fff',
        padding: '12px 16px',
        borderRadius: '8px',
        fontFamily: 'monospace',
        fontSize: '12px',
        zIndex: 1000,
        minWidth: '200px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
        <div style={{ fontWeight: 'bold', fontSize: '13px' }}>
          Map Debug Info
        </div>
        <button
          onClick={toggleDebugHexagons}
          style={{
            backgroundColor: showDebugHexagons ? 'rgba(255, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.1)',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            padding: '4px 8px',
            cursor: 'pointer',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color 0.2s',
          }}
          title={showDebugHexagons ? 'Hide debug hexagons' : 'Show debug hexagons'}
        >
          🐛
        </button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <div>
          <span style={{ color: '#888' }}>Zoom:</span>{' '}
          <span style={{ color: '#4CAF50' }}>{viewport.zoom.toFixed(2)}</span>
        </div>
        <div>
          <span style={{ color: '#888' }}>Center:</span>
        </div>
        <div style={{ paddingLeft: '12px' }}>
          <span style={{ color: '#888' }}>Lat:</span>{' '}
          <span style={{ color: '#2196F3' }}>{viewport.latitude.toFixed(6)}</span>
        </div>
        <div style={{ paddingLeft: '12px' }}>
          <span style={{ color: '#888' }}>Lng:</span>{' '}
          <span style={{ color: '#2196F3' }}>{viewport.longitude.toFixed(6)}</span>
        </div>
        
        {viewportStats && (
          <>
            <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(255, 255, 255, 0.2)' }}>
              <span style={{ color: '#888' }}>Viewport:</span>{' '}
              <span style={{ color: '#2196F3' }}>{viewportStats.viewportSqKm.toLocaleString(undefined, { maximumFractionDigits: 0 })} sq/km</span>
            </div>
            
            {viewportStats.res2Stats && (
              <div>
                <span style={{ color: '#888' }}>r2:</span>{' '}
                <span style={{ color: '#FF9800' }}>{viewportStats.res2Stats.percentage.toFixed(0)}%</span>
                {' '}
                <span style={{ color: '#666', fontSize: '11px' }}>
                  ({viewportStats.res2Stats.count.toLocaleString()}/{viewportStats.res2Stats.totalSqKm.toLocaleString(undefined, { maximumFractionDigits: 0 })} sq/km)
                </span>
              </div>
            )}
            
            {viewportStats.aggStats && (
              <div>
                <span style={{ color: '#888' }}>r{viewportStats.aggStats.resolution}:</span>{' '}
                <span style={{ color: '#FF9800' }}>{viewportStats.aggStats.percentage.toFixed(0)}%</span>
                {' '}
                <span style={{ color: '#666', fontSize: '11px' }}>
                  ({viewportStats.aggStats.count.toLocaleString()}/{viewportStats.aggStats.totalSqKm.toLocaleString(undefined, { maximumFractionDigits: 0 })} sq/km)
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
